import { DatePicker, Empty, Tooltip } from '@maxtropy/components';
import { Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { isNil } from 'lodash-es';
import React, { useContext, useEffect, useState } from 'react';
import { ShareConfigDataWaterContext } from '../../../..';
import FlowSwitchDate, { DateTypeByWater } from './FlowSwitchDate';
import styles from './index.module.scss';
import { useRequest } from 'ahooks';
import FlowEcharts from './FlowEcharts';
import { EnergyFlowChartNodeItem, getEnergyFlowChart } from '@/api/plustekSankey';

const filterSankeyData = (allNodes: EnergyFlowChartNodeItem[], fromLevel: number, toLevel: number) => {
  const nodes = [...allNodes].filter(i => i.depth + 1 >= fromLevel && i.depth + 1 <= toLevel);
  // 3. 重新映射 level，从 0 开始
  const minLevel = Math.min(...nodes.map(n => n.depth));
  return nodes.map(n => ({
    ...n,
    depth: n.depth - minLevel, // 重新映射
  }));
};

export function formatTime(time: [dayjs.Dayjs, dayjs.Dayjs], radioType: DateTypeByWater) {
  if (radioType === DateTypeByWater.DAY) {
    return {
      fromTime: time[0].format('YYYY-MM-DD'),
      toTime: time[1].format('YYYY-MM-DD'),
    };
  }
  return {
    fromTime: dayjs(time[0]).startOf('month').format('YYYY-MM-DD'),
    toTime: dayjs(time[1]).endOf('month').format('YYYY-MM-DD'),
  };
}

const RangePicker = DatePicker.RangePicker;
type RangeValue = [Dayjs | null, Dayjs | null] | null;

interface UseWaterEnergyFlowProps {
  modalMode?: boolean;
  onRouteParamsChange?: (params: { flowTimeResolution: DateTypeByWater; rangeTime?: RangeValue }) => void;
}

const UseWaterEnergyFlow = ({ modalMode = false, onRouteParamsChange }: UseWaterEnergyFlowProps) => {
  const { configDetail } = useContext(ShareConfigDataWaterContext);
  // const [unitGroupId, setUnitGroupId] = useState<number>();
  const [onlyKey, setOnlyKey] = useState<string>();
  const [flowTimeResolution, setFlowTimeResolution] = useState<DateTypeByWater>(DateTypeByWater.DAY);
  const [dates, setDates] = useState<RangeValue>(null);
  const [value, setValue] = useState<RangeValue>([dayjs().startOf('month'), dayjs()]);
  const [rangeTime, setRangeTime] = useState<RangeValue>();

  // format startDate endDate
  useEffect(() => {
    if (!flowTimeResolution) return;
    if (flowTimeResolution === DateTypeByWater.DAY) {
      setRangeTime([value?.[0]?.startOf('D') ?? null, value?.[1]?.endOf('D') ?? null]);
    } else {
      setRangeTime([value?.[0]?.startOf('month') ?? null, value?.[1] ?? null]);
    }
  }, [value, flowTimeResolution]);

  useEffect(() => {
    onRouteParamsChange?.({ flowTimeResolution, rangeTime });
  }, [flowTimeResolution, onRouteParamsChange, rangeTime]);

  // 用能趋势
  const { data: flowChartData, loading: flowChartLoading } = useRequest(
    async () => {
      const t = formatTime(rangeTime as [dayjs.Dayjs, dayjs.Dayjs], flowTimeResolution);
      const selectedMediumId = configDetail?.energyUnitGroupReqs?.find(
        i => i.energyGroupId + '-' + i.mediumId === onlyKey
      )?.mediumId;
      const res = await getEnergyFlowChart({
        ...t,
        energyUnitGroupId: onlyKey?.split('-')[0]!,
        energyMediumId: selectedMediumId,
      });
      let chartDataTemp = {
        nodes: (res.nodes ?? [])
          .map(item => ({
            ...item,
            name: item.key,
          }))
          .filter(c => !isNil(c.value) && c.value !== 0),
        links: res.links.filter(item => !isNil(item.value) && item.value !== 0),
      };
      const fromLevel = configDetail?.energyUnitGroupReqs?.find(
        item => item.energyGroupId + '-' + item.mediumId === onlyKey
      )?.fromLevel;
      const toLevel = configDetail?.energyUnitGroupReqs?.find(
        item => item.energyGroupId + '-' + item.mediumId === onlyKey
      )?.toLevel;
      if (fromLevel && toLevel) {
        return {
          nodes: filterSankeyData(chartDataTemp.nodes, fromLevel, toLevel),
          links: chartDataTemp.links,
        };
      }
    },
    {
      ready: !!onlyKey && !!rangeTime && !!flowTimeResolution,
      refreshDeps: [onlyKey, rangeTime, flowTimeResolution],
    }
  );

  // 默认选择一个用能分析组
  useEffect(() => {
    if (!configDetail) return;
    // setUnitGroupId(configDetail?.energyUnitGroupReqs?.[0]?.energyGroupId);
    setOnlyKey(
      configDetail?.energyUnitGroupReqs?.[0]?.energyGroupId + '-' + configDetail?.energyUnitGroupReqs?.[0]?.mediumId
    );
  }, [configDetail]);

  const disabledDate = (current: Dayjs) => {
    if (!dates) {
      return false;
    }
    const tooLate =
      flowTimeResolution === DateTypeByWater.DAY
        ? dates[0] && current.diff(dates[0], 'day') >= 90
        : dates[0] && current.diff(dates[0], 'month') >= 23;

    const tooEarly =
      flowTimeResolution === DateTypeByWater.DAY
        ? dates[1] && dates[1].diff(current, 'day') >= 90
        : dates[1] && dates[1].diff(current, 'month') >= 23;
    return !!tooEarly || !!tooLate || current.isAfter(dayjs());
  };

  const onOpenChange = (open: boolean) => {
    if (open) {
      setDates([null, null]);
    } else {
      setDates(null);
    }
  };
  const onRadioChange = (value: DateTypeByWater) => {
    setFlowTimeResolution(value);
    if (value === DateTypeByWater.DAY) {
      setValue([dayjs().subtract(3, 'day'), dayjs().subtract(1, 'day')]);
    } else {
      setValue([dayjs().startOf('month'), dayjs()]);
    }
  };

  // 时间跨度为3个月
  const customDisabledDate = (current: Dayjs) => {
    if (!dates) {
      return false;
    }
    const tooLate = dates[0] && current.diff(dates[0], 'months') >= 3;
    const tooEarly = current && current > dayjs().endOf('month');
    return !!tooEarly || !!tooLate;
  };

  // 时间跨度24个月
  const customDisabledMonthDate = (current: Dayjs) => {
    if (!dates) {
      return false;
    }
    return current && current >= dayjs().endOf('day');
  };

  return (
    <div className={`${styles.useEnergy_wrapper} ${modalMode ? styles.modalMode : ''}`}>
      <div className={styles.header}>
        <Space size={8}>
          {(configDetail?.energyUnitGroupReqs ?? []).map((item, index) => (
            <div
              key={index}
              className={`${styles.dateBtn} ${onlyKey === item.energyGroupId + '-' + item.mediumId && styles.active}`}
              onClick={() => {
                setOnlyKey(item.energyGroupId + '-' + item.mediumId);
                // setUnitGroupId(item.energyGroupId);
              }}
            >
              <Tooltip title={item.showEnergyGroupName}>{item.showEnergyGroupName}</Tooltip>
            </div>
          ))}
        </Space>
        {/* <Space size={8}>
            <FlowSwitchDate
              defaultValue={flowTimeResolution}
              onChange={onRadioChange}
              options={[
                { label: '按日', value: DateTypeByWater.DAY },
                { label: '按月', value: DateTypeByWater.MONTH },
              ]}
            />
            <RangePicker
              value={dates || value}
              getPopupContainer={() => document.getElementById('full_screen_box') ?? document.body}
              disabledDate={flowTimeResolution === DateTypeByWater.DAY ? customDisabledDate : customDisabledMonthDate}
              onCalendarChange={val => setDates(val)}
              onChange={val => {
                setValue(val);
              }}
              onOpenChange={onOpenChange}
              picker={flowTimeResolution === DateTypeByWater.MONTH ? 'month' : undefined}
              allowClear={false}
            />
          </Space> */}
      </div>
      <div className={styles.content}>
        {flowChartData ? (
          <FlowEcharts
            flowChartLoading={flowChartLoading}
            isMedium={
              configDetail?.energyUnitGroupReqs?.find(i => i.energyGroupId + '-' + i.mediumId === onlyKey)?.mediumId !==
              -1
            }
            data={flowChartData}
            physicalUnitName={
              configDetail?.energyUnitGroupReqs?.find(i => i.energyGroupId + '-' + i.mediumId === onlyKey)
                ?.physicalUnitName
            }
          />
        ) : (
          <Empty />
        )}
      </div>
    </div>
  );
};

export default UseWaterEnergyFlow;
